import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#3B5EDF";
const BLUE = "#3B5EDF";
const LIME = "#D9F154";
const CREAM = "#141414";
const BG = "#FAF7EF";
const BG_ALT = "#FFFFFF";
const BG_ELEV = "#FFFFFF";

const STATUS_LABELS = {
  coming_soon: "coming soon",
  admitted: "admitted",
  graduated: "graduated",
  closed: "facility closed",
};

const STATUS_COLORS = {
  coming_soon: `${CREAM}66`,
  admitted: "#9cc0ff",
  graduated: "#4ac1a7",
  closed: GOLD,
};

const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Archivo+Black&display=swap');`;

export default function Portfolio() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/portfolio`)
      .then(({ data }) => setCompanies(data.companies || []))
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
  }, []);

  const total = companies.length;
  const graduated = companies.filter((c) => c.status === "graduated" || c.status === "closed").length;

  return (
    <div
      data-testid="portfolio-page"
      style={{ background: BG, color: CREAM, minHeight: "100vh", fontFamily: "'Archivo', sans-serif" }}
    >
      <style>{`${fontImport} body { background: ${BG}; margin: 0; } * { box-sizing: border-box; }`}</style>

      {/* Nav */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "18px 32px",
          background: "rgba(14,13,11,0.95)",
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${CREAM}0e`,
        }}
      >
        <Link
          to="/"
          data-testid="portfolio-nav-home"
          style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "inherit" }}
        >
          <LogoMark size={34} />
          <div>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 17, fontWeight: 500, letterSpacing: "-0.02em", lineHeight: 1 }}>
              The Debt Guy
            </div>
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 8.5,
                letterSpacing: "0.3em",
                opacity: 0.55,
                marginTop: 3,
              }}
            >
              BY THE DEBT GUY
            </div>
          </div>
        </Link>
        <Link
          to="/"
          style={{
            fontFamily: "'Archivo', sans-serif",
            fontSize: 10.5,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: CREAM,
            textDecoration: "none",
            opacity: 0.75,
          }}
        >
          ← back to program
        </Link>
      </nav>

      {/* Hero */}
      <section style={{ padding: "120px 32px 72px", maxWidth: 1280, margin: "0 auto" }}>
        <div
          style={{
            fontFamily: "'Archivo', sans-serif",
            fontSize: 10.5,
            letterSpacing: "0.3em",
            color: GOLD,
            textTransform: "uppercase",
            marginBottom: 28,
          }}
        >
          — PORTFOLIO · COHORTS IN FLIGHT
        </div>
        <h1
          className="serif"
          style={{
            fontFamily: "'Archivo', sans-serif",
            fontSize: "clamp(44px, 7vw, 96px)",
            fontWeight: 300,
            lineHeight: 1,
            letterSpacing: "-0.035em",
            marginBottom: 30,
            textTransform: "lowercase",
          }}
        >
          the companies{" "}
          <span className="gold" style={{ color: GOLD, fontStyle: "normal", fontWeight: 400 }}>
            raising debt
          </span>
          <br />
          with the debt guy.
        </h1>
        <p style={{ fontSize: 17, lineHeight: 1.6, opacity: 0.75, maxWidth: 720, fontWeight: 300 }}>
          We publish outcomes, not certificates. As cohort members close their first institutional facility, they land here —
          with the allocator, facility size and time-to-close on record.
        </p>

        {/* Aggregate counters */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginTop: 56,
            paddingTop: 40,
            borderTop: `1px solid ${CREAM}14`,
          }}
        >
          <div data-testid="portfolio-stat-cohort">
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 9.5,
                letterSpacing: "0.25em",
                color: GOLD,
                textTransform: "uppercase",
                opacity: 0.75,
                marginBottom: 10,
              }}
            >
              active batch
            </div>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 40, fontWeight: 300, letterSpacing: "-0.03em" }}>
              Q4 2026
            </div>
          </div>
          <div data-testid="portfolio-stat-total">
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 9.5,
                letterSpacing: "0.25em",
                color: GOLD,
                textTransform: "uppercase",
                opacity: 0.75,
                marginBottom: 10,
              }}
            >
              companies in flight
            </div>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 40, fontWeight: 300, letterSpacing: "-0.03em" }}>
              {total > 0 ? total : "—"}
            </div>
          </div>
          <div data-testid="portfolio-stat-graduated">
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 9.5,
                letterSpacing: "0.25em",
                color: GOLD,
                textTransform: "uppercase",
                opacity: 0.75,
                marginBottom: 10,
              }}
            >
              facilities closed
            </div>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 40, fontWeight: 300, letterSpacing: "-0.03em" }}>
              {graduated > 0 ? graduated : "—"}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section style={{ padding: "20px 32px 120px", maxWidth: 1280, margin: "0 auto" }}>
        {loading ? (
          <div style={{ opacity: 0.6, padding: 40, textAlign: "center" }} data-testid="portfolio-loading">
            loading…
          </div>
        ) : companies.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            data-testid="portfolio-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 2,
              background: `${CREAM}11`,
              border: `1px solid ${CREAM}11`,
            }}
          >
            {companies.map((c) => (
              <CompanyCard key={c.id} c={c} />
            ))}
          </div>
        )}
      </section>

      <footer
        style={{
          padding: "40px 32px",
          borderTop: `1px solid ${CREAM}0e`,
          textAlign: "center",
          fontFamily: "'Archivo', sans-serif",
          fontSize: 10,
          letterSpacing: "0.25em",
          opacity: 0.45,
          textTransform: "uppercase",
        }}
      >
        The Debt Guy · © 2026
      </footer>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="portfolio-empty"
      style={{
        border: `1px dashed ${GOLD}55`,
        padding: "72px 32px",
        textAlign: "center",
        background: BG_ALT,
      }}
    >
      <div
        style={{
          fontFamily: "'Archivo', sans-serif",
          fontSize: 10,
          letterSpacing: "0.3em",
          color: GOLD,
          textTransform: "uppercase",
          marginBottom: 22,
        }}
      >
        — cohort 0 · Q4 2026
      </div>
      <h2
        style={{
          fontFamily: "'Archivo', sans-serif",
          fontSize: 42,
          fontWeight: 300,
          letterSpacing: "-0.03em",
          marginBottom: 18,
          lineHeight: 1.1,
        }}
      >
        the first batch is being selected.
      </h2>
      <p style={{ fontSize: 16, opacity: 0.72, lineHeight: 1.6, maxWidth: 560, margin: "0 auto 28px", fontWeight: 300 }}>
        We'll publish each admitted company here as they join the batch, and their facility as it closes — with the
        allocator, size and time-to-close on record.
      </p>
      <Link
        to="/"
        data-testid="portfolio-apply-cta"
        style={{
          display: "inline-block",
          background: GOLD,
          color: BG,
          padding: "14px 28px",
          fontFamily: "'Archivo', sans-serif",
          fontSize: 10.5,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 500,
          textDecoration: "none",
        }}
      >
        apply to join
      </Link>
    </div>
  );
}

function CompanyCard({ c }) {
  const statusColor = STATUS_COLORS[c.status] || CREAM;
  const statusLabel = STATUS_LABELS[c.status] || c.status;
  return (
    <div
      data-testid={`portfolio-card-${(c.name || "").toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        background: BG_ALT,
        padding: "28px 26px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        minHeight: 260,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {c.logo_url ? (
            <div
              style={{
                background: "#fff",
                padding: "12px 14px",
                display: "inline-flex",
                marginBottom: 16,
                maxWidth: 160,
              }}
            >
              <img src={c.logo_url} alt={c.name} style={{ maxHeight: 36, maxWidth: 140, objectFit: "contain" }} />
            </div>
          ) : (
            <div
              style={{
                width: 52,
                height: 52,
                background: `linear-gradient(135deg, ${BG_ELEV}, ${BG})`,
                border: `1px solid ${GOLD}33`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: GOLD,
                fontFamily: "'Archivo', sans-serif",
                fontSize: 22,
                fontWeight: 400,
                marginBottom: 14,
              }}
            >
              {(c.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <h3
            style={{
              fontFamily: "'Archivo', sans-serif",
              fontSize: 24,
              fontWeight: 400,
              letterSpacing: "-0.01em",
              lineHeight: 1.15,
              marginBottom: 4,
            }}
          >
            {c.name}
          </h3>
          {c.asset_class && (
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 9.5,
                letterSpacing: "0.18em",
                color: GOLD,
                textTransform: "uppercase",
                opacity: 0.85,
                marginBottom: 3,
              }}
            >
              {c.asset_class}
            </div>
          )}
          {c.geography && (
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{c.geography}</div>
          )}
        </div>
        <span
          style={{
            fontFamily: "'Archivo', sans-serif",
            fontSize: 8.5,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            padding: "4px 9px",
            border: `1px solid ${statusColor}66`,
            color: statusColor,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {statusLabel}
        </span>
      </div>

      {c.blurb && (
        <p style={{ fontSize: 13.5, opacity: 0.72, lineHeight: 1.55, fontWeight: 300 }}>{c.blurb}</p>
      )}

      <div
        style={{
          marginTop: "auto",
          paddingTop: 18,
          borderTop: `1px solid ${CREAM}14`,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
        }}
      >
        {c.facility_size_usd && (
          <div>
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 8.5,
                letterSpacing: "0.2em",
                opacity: 0.5,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              facility
            </div>
            <div style={{ fontSize: 14, color: GOLD, fontWeight: 500 }}>{c.facility_size_usd}</div>
          </div>
        )}
        {c.time_to_close_days != null && (
          <div>
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 8.5,
                letterSpacing: "0.2em",
                opacity: 0.5,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              time to close
            </div>
            <div style={{ fontSize: 14 }}>{c.time_to_close_days} days</div>
          </div>
        )}
        {c.anchor_allocator && (
          <div style={{ gridColumn: "1 / -1" }}>
            <div
              style={{
                fontFamily: "'Archivo', sans-serif",
                fontSize: 8.5,
                letterSpacing: "0.2em",
                opacity: 0.5,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              anchor allocator
            </div>
            <div style={{ fontSize: 13 }}>{c.anchor_allocator}</div>
          </div>
        )}
        {c.website && (
          <a
            href={c.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              gridColumn: "1 / -1",
              fontFamily: "'Archivo', sans-serif",
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: CREAM,
              opacity: 0.55,
              textDecoration: "none",
              paddingTop: 4,
            }}
          >
            visit site →
          </a>
        )}
      </div>
    </div>
  );
}
